import {Button, CircularProgress, Paper} from '@mui/material';
import {DateTime} from 'luxon';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {getArea} from 'ol/extent';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {
  SearchQuery,
  useGetCatalogSearch,
  useGetStacCollection,
} from '@src/api/catalog';
import {useGetConfigurationCollections} from '@src/api/configurations';
import {notifyError} from '@src/components/notifier/notify';
import {WORLD_EXTENT} from '@src/constants';
import {ANALYZE_VISUALIZE_ROUTE} from '@src/constants/routes';
import {serialize as serializeDateTime} from '@src/hooks/useDateTimeParams';
import useVisualizationParams, {
  URL_KEY_MAP,
} from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions';
import {AppLocationState} from '@src/types/appLocation';
import {StacCollection} from '@src/types/configurations';
import {parseDateTimeFromCollectionWithDefaults} from '@src/utils/catalog.ts';

const parseExtentBboxFromCollection = (collection?: StacCollection) => {
  return collection?.extent?.spatial?.bbox?.[0] ?? WORLD_EXTENT;
};

export const VisualizeButton = () => {
  const navigate = useNavigate();
  const {
    collectionId,
    configurationId,
    serialize: serializeVisualizationParams,
  } = useVisualizationParams();
  const {collections} = useGetConfigurationCollections(configurationId);
  const selectedCollectionId = collectionId ?? collections[0]?.id;
  const searchParams = useOptimisticSearchParams();
  const {data: collection} = useGetStacCollection(selectedCollectionId);
  const collectionsBBox = parseExtentBboxFromCollection(collection);
  const collectionsDateRange =
    parseDateTimeFromCollectionWithDefaults(collection);
  const [searchQueryParams, setSearchQueryParams] = useState<
    SearchQuery | undefined
  >();

  const {data: searchResults, isLoading: isLoadingResults} =
    useGetCatalogSearch(searchQueryParams!, {enabled: !!searchQueryParams});

  const {bbox: firstResultBBox, properties} =
    searchResults?.features?.[0] || {};
  const firstResultDate = properties?.datetime;

  const isResultAreaMuchSmallerThanEntireCollection =
    firstResultBBox &&
    getArea(firstResultBBox) / getArea(collectionsBBox) < 0.001;
  const fitToExtent = isResultAreaMuchSmallerThanEntireCollection
    ? firstResultBBox
    : collectionsBBox;

  useEffect(() => {
    if (searchResults && fitToExtent?.length === 4) {
      try {
        searchParams.delete(URL_KEY_MAP.superRes);
        searchParams.delete(URL_KEY_MAP.confidenceThreshold);
        const defaultVisualizeParameters = new URLSearchParams(
          serializeVisualizationParams({
            collectionId: selectedCollectionId,
            configurationId,
            layerId: '',
          }),
        );

        const dateTimeParams = firstResultDate
          ? new URLSearchParams(
              serializeDateTime({
                dateTimeFrom:
                  DateTime.fromISO(firstResultDate ?? '')
                    .startOf('day')
                    .toISO() ?? null,
                dateTimeTo:
                  DateTime.fromISO(firstResultDate ?? '')
                    .endOf('day')
                    .toISO() ?? null,
              }),
            )
          : new URLSearchParams();

        const navigateParams = new URLSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          ...Object.fromEntries(defaultVisualizeParameters.entries()),
          ...Object.fromEntries(dateTimeParams.entries()),
        });
        navigate(
          {
            pathname: `/${ANALYZE_VISUALIZE_ROUTE}`,
            search: navigateParams.toString(),
          },
          {
            state: {
              mapExtent: {
                bbox: fitToExtent,
                duration: 300,
              },
              tileBbox: fitToExtent,
            } satisfies AppLocationState,
          },
        );
      } catch (error) {
        notifyError({
          data: {
            message: 'Error fetching catalog data',
          },
        });
      } finally {
        setSearchQueryParams(undefined);
      }
    }
  }, [
    navigate,
    searchResults,
    searchParams,
    serializeVisualizationParams,
    selectedCollectionId,
    configurationId,
    fitToExtent,
    firstResultDate,
  ]);

  const isDisabled =
    !configurationId || !selectedCollectionId || isLoadingResults;
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'sticky',
        display: 'flex',
        insetBlockEnd: -0.5,
        paddingBlockStart: 1.5,
        paddingBlockEnd: 1,
        paddingInline: `${dimensions.leftSidebar.contentMargin}px`,
      }}
    >
      <Button
        onClick={() => {
          if (!collection) {
            return;
          }
          setSearchQueryParams({
            bbox: collectionsBBox,
            datetime: `${collectionsDateRange.to.minus({months: 1}).toISO()}/${collectionsDateRange.to.toISO()}`,
            collections: [collection.id],
            limit: 10,
          });
        }}
        disabled={isDisabled}
        variant="contained"
        color="primary"
        size="large"
        sx={{flexGrow: 1}}
      >
        {isLoadingResults ? (
          <CircularProgress size={24} sx={{marginBlock: 0.15}} />
        ) : (
          'Visualize'
        )}
      </Button>
    </Paper>
  );
};

export default VisualizeButton;
